// role-based-dashboards — Phase 6.1: org_admin overview "30d KPI row",
// "30d volume/revenue trend" and "per-location breakdown table" widgets.
//
// Reuses the EXISTING analytics presentational components verbatim
// (OrgRollup / LocationBreakdownTable / TrendsSection, all already extracted
// by admin-analytics-expansion) — no duplication, per design "StatCard...
// ALREADY at packages/ui... Extraction is DROPPED from scope".
//
// Gating (design "org_admin widgets... each wrapped in a per-widget error
// boundary → 'Upgrade' placeholder"): `getOrgAnalytics` and `getTicketTrends`
// are both gated behind the SAME `advancedAnalytics` plan feature server-side,
// so a single `getOrgAnalytics` 403 check covers the whole KPI/breakdown/trend
// group — this avoids firing (and 403-ing) a second `getTicketTrends` request
// when we already know the org is locked out, while still keeping the
// "independently, without blocking other widgets" guarantee for the UNGATED
// widgets (entity strip, subscription card, feeds), which live in sibling
// components entirely outside this one.
//
// FIX 3 (review) — `getOrgAnalytics` returning 403 (no `advancedAnalytics` on
// the plan) is an EXPECTED outcome, not a transient failure. The default
// `retry: 3` (with backoff) would otherwise fire 3 extra doomed requests and
// keep the skeleton up for several seconds before the upgrade placeholder
// ever shows. `retryOnNonClientError` skips retrying any 4xx.
//
// FIX 4 (review) — only the 403/upgrade-gate case swaps the WHOLE group for
// `UpgradePlaceholder` (both `getOrgAnalytics` and `getTicketTrends` are
// gated behind the same plan feature, so there is nothing useful to show
// underneath it). A non-403 error (network blip, 5xx) instead renders a
// scoped error state for the rollup/breakdown only — `TrendsSection` keeps
// its own independent `getTicketTrends` query and is NOT hidden by it.

import { ApiError, getOrgAnalytics } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { LocationResponse } from '@jq4race/shared';
import { Button, Skeleton } from '@jq4race/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Lock, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { retryOnNonClientError } from '../../../../shared/query/retryOnNonClientError';
import { LocationBreakdownTable } from '../../../analytics/ui/components/LocationBreakdownTable';
import { OrgRollup } from '../../../analytics/ui/components/OrgRollup';
import { TrendsSection } from '../../../analytics/ui/components/TrendsSection';

const RANGE_DAYS = 30;

function last30dRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - RANGE_DAYS);
  return { from: from.toISOString(), to: to.toISOString() };
}

export interface OrgAnalyticsOverviewSectionProps {
  locations: LocationResponse[];
}

export function OrgAnalyticsOverviewSection({ locations }: OrgAnalyticsOverviewSectionProps) {
  const { t } = useTranslation('dashboard');
  // FIX (review, discovered while writing FIX 3/4/5 tests) — `last30dRange()`
  // MUST be computed once per mount, not inline in the render body. Calling
  // `new Date()` on every render produces a new millisecond-precision
  // queryKey on every render, which React Query treats as a BRAND NEW query
  // — discarding the in-flight one before it can ever settle. In the success
  // path this was silently masked by `placeholderData: keepPreviousData`
  // (the last good render stays visible while new queries churn in the
  // background), but on any error path there is no prior data to keep, so
  // the component gets stuck re-issuing fresh requests forever and never
  // reaches `isError`. Mirrors the established `useState(defaultRange)` lazy
  // initializer convention already used by `AnalyticsPage.tsx`.
  const [{ from, to }] = useState(last30dRange);

  const {
    data: orgKpis,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['dashboard-overview', 'org-analytics', from, to],
    queryFn: () => getOrgAnalytics({ from, to }),
    placeholderData: keepPreviousData,
    // FIX 3 (review) — don't burn 3 retries + backoff on an EXPECTED 403.
    retry: retryOnNonClientError,
  });

  const locationNameById = new Map(locations.map((loc) => [loc.id, loc.name]));

  const isUpgradeGate = isError && error instanceof ApiError && error.status === 403;

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="jq-eyebrow">{t('orgAnalytics.eyebrow')}</p>
        <Button asChild variant="outline" size="sm" className="text-[13px]">
          <Link to="/dashboard/analytics">{t('orgAnalytics.viewFullAnalytics')}</Link>
        </Button>
      </div>

      {isUpgradeGate ? (
        // Whole group gated: getTicketTrends shares the same plan-feature
        // gate, so there is nothing useful left to render underneath.
        <UpgradePlaceholder />
      ) : (
        // FIX 4 (review) — a non-403 error only replaces the rollup/breakdown
        // with a scoped error state; TrendsSection keeps its own independent
        // getTicketTrends query and renders regardless (loading/error/success
        // handled entirely by TrendsSection itself).
        <div className="space-y-6">
          {isError ? (
            <SectionErrorState error={error} />
          ) : isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Skeleton className="h-[104px]" />
              <Skeleton className="h-[104px]" />
              <Skeleton className="h-[104px]" />
            </div>
          ) : (
            <>
              <OrgRollup total={orgKpis?.total} />
              <LocationBreakdownTable
                rows={orgKpis?.byLocation}
                locationNameById={locationNameById}
              />
            </>
          )}

          {/* Delayed until getOrgAnalytics settles (not rendered while
              `isLoading`): mounting it eagerly would fire `getTicketTrends`
              before we even know whether the org is upgrade-gated, defeating
              the "single 403 check covers the whole group" guarantee above —
              it would 403 once anyway before the gate could hide it. Once we
              reach here, `isUpgradeGate` is already known false, so this is
              never a wasted request. */}
          {!isLoading && <TrendsSection from={from} to={to} />}
        </div>
      )}
    </section>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function UpgradePlaceholder() {
  const { t } = useTranslation('dashboard');
  return (
    <div className="flex flex-col items-center gap-3 rounded-sm border border-jq-sand/60 bg-jq-surface-0 px-6 py-10 text-center">
      <Lock className="h-5 w-5 text-jq-gold-deep" strokeWidth={1.5} />
      <p className="text-[14px] font-medium text-jq-charcoal">{t('orgAnalytics.upgradeTitle')}</p>
      <p className="max-w-md text-[13px] text-jq-graphite">{t('orgAnalytics.upgradeBody')}</p>
      <Button asChild variant="outline" size="sm" className="text-[13px]">
        <Link to="/dashboard/settings/subscription">{t('orgAnalytics.viewPlans')}</Link>
      </Button>
    </div>
  );
}

function SectionErrorState({ error }: { error: Error | null }) {
  const { t } = useTranslation('dashboard');
  const message = translateApiError(error, t, { fallback: t('orgAnalytics.error') });
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-4 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="leading-snug">{message}</p>
    </div>
  );
}
